import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import ComercialDropDown from '../obras/inputs/NewComercialDropDown';
import EstadoObraDropdown from '../obras/inputs/EstadoObraDropdown';
import TipoMaterialDropdown from '../obras/inputs/TipoMaterialDropdown';
import ConstructoraDropdown from '../obras/inputs/NewConstructoraDropdown';
import SegmentoDropdown from '../obras/inputs/SegmentoDropdown';
import CiudadDropDown from '../obras/inputs/CiudadDropDown01';
import ZonaDropDown from '../obras/inputs/ZonaDropDown';
import DelegacionDropDown from '../obras/inputs/DelegacionDropDown';
import ClienteDropdown from '../obras/inputs/ClienteDropdown';
import ClienteDropdownSubcontrata from '../obras/inputs/ClienteDropdownSubcontrata';
import TipoObraDropdown from '../obras/inputs/TipoObraDropdown';
import MapaUbicacion from './inputs/MapaUbicacion';
import axios from 'axios';
import { getApiUrl } from '../../utils/apiConfig';
import { usePermisos } from '../../context/PermisosContext';
import AdminReadOnlyAlert from '../AdminReadOnlyAlert';

import { CircleCheck, CircleX } from 'lucide-react';

const formatCurrency = (value) => {
  if (value === '' || value === null || value === undefined || isNaN(value)) return '';
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(value);

  return formatted.replace('€', '€ ');
};

const todayLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const emptySubcontrata = () => ({
  IdCliente: null,
  nombre: '',
  especialidad: '',
  contacto: { nombre: '', telefono: '', email: '' },
  Comentarios: ''
});

const ObraContent = ({ obra, setObra, onSave, onCancel, user, comercialIdsPermitidos, infoComerciales, ubicacionesPermitidas, idsSeccionesPermitidos, refreshVisitas, refreshObras }) => {
  const navigate = useNavigate();
  const { isAdmin } = usePermisos(); // 🔒 Obtener si es admin
  const [modificado, setModificado] = useState(false);
  const [originalObra, setOriginalObra] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [subcontratas, setSubcontratas] = useState([emptySubcontrata()]);
  const [subcontratatasOriginales, setSubcontratatasOriginales] = useState([]);
  const [emailError, setEmailError] = useState('');
  const [subcontrataEmailErrors, setSubcontrataEmailErrors] = useState({});
  const [tiposObra, setTiposObra] = useState([]);
  const [visitaCreadaId, setVisitaCreadaId] = useState(null); // Para almacenar el ID de la visita creada
  const [creandoVisitaIdx, setCreandoVisitaIdx] = useState(null); // Para rastrear qué subcontrata está procesando
  const [loadingSubcontratas, setLoadingSubcontratas] = useState(false); // Para mostrar estado de carga

  // Inicializar obra original para detectar cambios
  useEffect(() => {
    if (obra) {
      setOriginalObra(JSON.stringify(obra));
      setSuccessMsg('');
      setErrorMsg('');
    }
  }, [obra?.IdObra]);

  // Cargar tipos de obra para validación
  useEffect(() => {
    const loadTipos = async () => {
      try {
        const res = await axios.get(`${getApiUrl('/api/tiposObra')}`);
        setTiposObra(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error("Error cargando tipos de obra:", e);
        setTiposObra([]);
      }
    };
    loadTipos();
  }, []);

  // 🔍 LOG: Verificar qué llega en la prop obra
  useEffect(() => {
    if (obra) {
      // console.log('📥 ObraEditar recibió obra:', obra);
      // console.log('📥 ObraEditar - Subdivision:', obra.Subdivision);
      // console.log('📥 ObraEditar - Delegacion:', obra.Delegacion);
      // console.log('📥 ObraEditar - Seccion:', obra.Seccion);
    }
  }, [obra]);

  // Cargar subcontratas cuando se abre la obra
  useEffect(() => {
    const cargarSubcontratas = async () => {
      if (!obra?.IdObra) return;
      
      setLoadingSubcontratas(true); // 🔄 Iniciar estado de carga
      
      // 🧪 Delay artificial para testing en local (simula carga lenta de producción)
      // await new Promise(resolve => setTimeout(resolve, 2000)); // Descomentar para probar
      
      try {
        const response = await fetch(`${getApiUrl('/api/subcontratas')}/${obra.IdObra}`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            // Mapear del formato backend al formato frontend
            const subcontratatasFormateadas = data.map(sc => ({
              IdSubcontrata: sc.IdSubcontrata,
              IdCliente: sc.IdCliente,
              nombre: sc.Nombre,
              especialidad: sc.Especialidad,
              contacto: {
                nombre: sc.NombreC,
                telefono: sc.TelefonoC,
                email: sc.EmailC
              },
              Comentarios: sc.Comentarios || ''
            }));
            setSubcontratas(subcontratatasFormateadas);
            setSubcontratatasOriginales(JSON.parse(JSON.stringify(subcontratatasFormateadas)));
          }
        }
      } catch (error) {
        console.error('Error al cargar subcontratas:', error);
      } finally {
        setLoadingSubcontratas(false); // ✅ Finalizar estado de carga
      }
    };

    cargarSubcontratas();
  }, [obra?.IdObra]);

  const handleChange = (campo, valor) => {
    setObra({ ...obra, [campo]: valor });
    setModificado(true);
  };

  // Detectar si hay cambios en obra o subcontratas
  const isModified = 
    originalObra !== JSON.stringify(obra) || 
    JSON.stringify(subcontratatasOriginales) !== JSON.stringify(subcontratas);

  if (!obra || typeof obra !== 'object' || Object.keys(obra).length === 0) return null;

  const esComercialAsignado = infoComerciales.some(c => c.id === obra.IdComercial);
  // console.log('obra.IdComercial:', obra.IdComercial);
  // console.log('infoComerciales ids:', infoComerciales.map(c => c.id));
  // console.log('¿Es comercial asignado?', esComercialAsignado);

  const addSubcontrata = () => {
    setSubcontratas(prev => {
      if (prev.length >= 5) return prev;
      return [
        ...prev,
        {
          IdCliente: null,
          nombre: '',
          especialidad: '',
          contacto: { nombre: '', telefono: '', email: '' },
          Comentarios: ''
        }
      ];
    });
  };

  const updateSubcontrata = (index, path, value) => {
    setSubcontratas(prev =>
      prev.map((sc, i) => {
        if (i !== index) return sc;
        if (path.startsWith('contacto.')) {
          const campo = path.split('.')[1];
          return { ...sc, contacto: { ...sc.contacto, [campo]: value } };
        }
        return { ...sc, [path]: value };
      })
    );
    setModificado(true);
  };

  const removeSubcontrata = (index) => {
    setSubcontratas(prev => {
      if (prev.length <= 1) {
        const copia = [...prev];
        copia[0] = emptySubcontrata();
        return copia;
      }
      return prev.filter((_, i) => i !== index);
    });
    setModificado(true);
  };

  const handleSave = async (autoClose = true) => {
    // Validar email del contacto visitado
    if (emailError) {
      setErrorMsg('Corrige el email del contacto visitado antes de guardar');
      return false;
    }

    // Validar emails de subcontratas
    const hasSubcontrataEmailErrors = Object.keys(subcontrataEmailErrors).length > 0;
    if (hasSubcontrataEmailErrors) {
      setErrorMsg('Corrige los emails de las subcontratas antes de guardar');
      return false;
    }

    // Validar campos obligatorios de la obra
    const camposObligatorios = [
      'FechaVisita',
      'IdComercial',
      'TecnicoComercial',
      'IdUbicacion',
      'Obra',
      'NombreCV',
      'TelefonoCV',
      'EmailCV',
      // 'NumeroDireccion',
      'EstadoObra',
      'TipoMaterial'
    ];

    const camposFaltantes = camposObligatorios.filter(campo => !obra[campo]);
    
    // Validación condicional de TipoObraInfoExtra basada en el tipo seleccionado
    const tipoSeleccionado = tiposObra.find(t => t.IdTipoObra === obra.IdTipoObra);
    if (tipoSeleccionado) {
      const nombre = (tipoSeleccionado.Nombre || '').trim().toUpperCase();
      const extraEtiqueta = tipoSeleccionado.ExtraEtiqueta?.trim();
      
      // Requiere campo extra si es: Obra Pública, Otros, Reforma Integral, o tiene ExtraEtiqueta
      const requiereInfoExtra = 
        nombre === 'OBRA PÚBLICA' ||
        nombre === 'OTROS' ||
        nombre === 'REFORMA INTEGRAL' ||
        Boolean(extraEtiqueta);
      
      if (requiereInfoExtra) {
        const infoExtra = obra.TipoObraInfoExtra || '';
        if (!infoExtra || String(infoExtra).trim() === '') {
          camposFaltantes.push('TipoObraInfoExtra');
        }
      }
    }
    
    if (camposFaltantes.length > 0) {
      const nombresLegibles = {
        'FechaVisita': 'Fecha de Visita',
        'IdComercial': 'Comercial',
        'TecnicoComercial': 'Técnico Comercial',
        'IdUbicacion': 'Ubicación',
        'Obra': 'Nombre de la Obra',
        'NombreCV': 'Nombre del Contacto',
        'TelefonoCV': 'Teléfono del Contacto',
        'EmailCV': 'Email del Contacto',
        // 'NumeroDireccion': 'Número',
        'EstadoObra': 'Estado de Obra',
        'TipoMaterial': 'Tipo de Material',
        'TipoObraInfoExtra': 'Información adicional del Tipo de Obra'
      };
      
      const camposLegibles = camposFaltantes.map(c => nombresLegibles[c] || c);
      
      // Usar un separador especial para distinguir el título de la lista
      const listaCampos = camposLegibles.map(c => `• ${c}`).join('\n');
      
      setErrorMsg(`TITULO||Faltan campos obligatorios:||LISTA||${listaCampos}`);
      return false;
    }

    setSaving(true);
    setErrorMsg('');

    try {
      // 1. Actualizar la obra
      const obraPayload = {
        IdComercial: obra.IdComercial,
        IdConstructora: obra.IdConstructora,
        JefeObra: obra.JefeObra || null,
        TelefonoC: obra.TelefonoC || null,
        ObservacionesC: obra.ObservacionesC || null,
        IdUbicacion: obra.IdUbicacion,
        IdCliente: obra.IdCliente || null,
        IdSegmento: obra.IdSegmento || null,
        NombreCV: obra.NombreCV || null,
        TelefonoCV: obra.TelefonoCV || null,
        EmailCV: obra.EmailCV || null,
        ObservacionesCV: obra.ObservacionesCV || null,
        NumeroDireccion: obra.NumeroDireccion || null,
        Obra: obra.Obra,
        Ofertado: obra.Ofertado || null,
        TecnicoComercial: obra.TecnicoComercial || null,
        FechaVisita: obra.FechaVisita,
        IdTipoObra: obra.IdTipoObra || null,
        TipoObraInfoExtra: obra.TipoObraInfoExtra || null,
        EstadoObra: obra.EstadoObra,
        NOferta: obra.NOferta || null,
        Otros: obra.Otros || null,
        Direccion: obra.Direccion || null,
        LatLong: obra.LatLong || null,
        TipoMaterial: obra.TipoMaterial || null
      };

      const obraResponse = await fetch(`${getApiUrl('/api/editObra')}/${obra.IdObra}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(obraPayload)
      });

      if (!obraResponse.ok) {
        throw new Error('Error al actualizar la obra');
      }

      // 2. Procesar subcontratas
      // console.log('📋 Estado actual de subcontratas:', subcontratas);
      // console.log('📋 Subcontratas originales:', subcontratatasOriginales);
      
      // Identificar subcontratas nuevas, actualizadas y eliminadas
      const subcontratatasNuevas = subcontratas.filter(sc => !sc.IdSubcontrata);
      const subcontratatasActualizadas = subcontratas.filter(sc => sc.IdSubcontrata);
      const idsActuales = subcontratatasActualizadas.map(sc => sc.IdSubcontrata);
      const subcontratatasEliminadas = subcontratatasOriginales.filter(
        orig => !idsActuales.includes(orig.IdSubcontrata)
      );

      // console.log('🆕 Subcontratas NUEVAS (sin IdSubcontrata):', subcontratatasNuevas);
      // console.log('♻️ Subcontratas ACTUALIZADAS (con IdSubcontrata):', subcontratatasActualizadas);
      // console.log('🗑️ Subcontratas ELIMINADAS:', subcontratatasEliminadas);

      // Crear nuevas subcontratas
      if (subcontratatasNuevas.length > 0) {
        // console.log('🆕 Subcontratas nuevas detectadas:', subcontratatasNuevas);
        
        const subcontratatasValidas = subcontratatasNuevas.filter(sc =>
          sc.nombre?.trim() &&
          sc.especialidad?.trim()
        );

        // console.log('✅ Subcontratas válidas para crear:', subcontratatasValidas);

        if (subcontratatasValidas.length > 0) {
          const subcontratatasPayload = subcontratatasValidas.map(sc => ({
            IdCliente: sc.IdCliente || null,
            Nombre: sc.nombre,
            Especialidad: sc.especialidad,
            NombreC: sc.contacto.nombre || null,
            TelefonoC: sc.contacto.telefono || null,
            EmailC: sc.contacto.email || null,
            Comentarios: sc.Comentarios || null
          }));

          // console.log('📤 Enviando payload al backend:', subcontratatasPayload);

          const createResponse = await fetch(`${getApiUrl('/api/subcontratas')}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              IdObra: obra.IdObra,
              subcontratas: subcontratatasPayload
            })
          });

          if (!createResponse.ok) {
            const errorData = await createResponse.json();
            console.error('❌ Error al crear subcontratas:', errorData);
            throw new Error(`Error al crear subcontratas: ${errorData.message || 'Error desconocido'}`);
          }

          const createResult = await createResponse.json();
          // console.log('✅ Subcontratas creadas exitosamente:', createResult);
        }
      }

      // Actualizar subcontratas existentes
      for (const sc of subcontratatasActualizadas) {
        if (sc.IdSubcontrata) {
          await fetch(`${getApiUrl('/api/subcontratas')}/${sc.IdSubcontrata}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              IdCliente: sc.IdCliente || null,
              Nombre: sc.nombre,
              Especialidad: sc.especialidad,
              NombreC: sc.contacto.nombre,
              TelefonoC: sc.contacto.telefono,
              EmailC: sc.contacto.email,
              Comentarios: sc.Comentarios || null
            })
          });
        }
      }

      // Eliminar subcontratas
      for (const sc of subcontratatasEliminadas) {
        if (sc.IdSubcontrata) {
          await fetch(`${getApiUrl('/api/subcontratas')}/${sc.IdSubcontrata}`, {
            method: 'DELETE'
          });
        }
      }

      // Recargar subcontratas actualizadas
      const reloadResponse = await fetch(`${getApiUrl('/api/subcontratas')}/${obra.IdObra}`);
      if (reloadResponse.ok) {
        const data = await reloadResponse.json();
        if (data.length > 0) {
          const subcontratatasFormateadas = data.map(sc => ({
            IdSubcontrata: sc.IdSubcontrata,
            IdCliente: sc.IdCliente,
            nombre: sc.Nombre,
            especialidad: sc.Especialidad,
            contacto: {
              nombre: sc.NombreC,
              telefono: sc.TelefonoC,
              email: sc.EmailC
            },
            Comentarios: sc.Comentarios || ''
          }));
          setSubcontratas(subcontratatasFormateadas);
          setSubcontratatasOriginales(JSON.parse(JSON.stringify(subcontratatasFormateadas)));
        }
      }
      
      // 🔄 Refrescar la lista de obras en el contexto
      if (refreshObras) {
        await refreshObras();
      }
      
      setSuccessMsg('Obra actualizada correctamente');
      setModificado(false);
      
      if (autoClose) {
        setTimeout(() => {
          setSuccessMsg('');
          if (onSave) onSave();
        }, 1500);
      } else {
        setTimeout(() => {
          setSuccessMsg('');
        }, 1500);
      }
      
      return true;

    } catch (error) {
      console.error('Error al guardar:', error);
      setErrorMsg(error.message || 'Error al guardar la obra. Intenta de nuevo.');
      setTimeout(() => setErrorMsg(''), 3000);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return (
    // <div className="flex flex-col gap-4 w-full max-w-full no-select">
    <div className="p-4 sm:p-6 lg:p-6 bg-gray-50 min-h-screen no-select">
      <div className="max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 my-4 sm:gap-0 sm:justify-between sm:items-center">
        <div className="flex items-center gap-6">
          {/* Botón volver */}
          <button
            onClick={onCancel}
            className="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors"
            title="Volver a Obras"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>
          
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Obras</h1>
            <p className="text-gray-600 text-sm sm:text-base">Editar obra</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3 bg-white px-3 sm:px-4 py-2 sm:py-3 rounded-xl shadow-sm border hover:shadow-lg hover:border-blue-200 transition-all duration-300 cursor-pointer">
          <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="text-right">
            <p className="font-medium text-gray-800 text-xs sm:text-sm md:text-base truncate max-w-[100px] sm:max-w-[150px] md:max-w-none">
              {user?.name || 'Usuario'}
            </p>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <div className="w-full">

        {/* 🔒 Alerta para admins */}
        {isAdmin && <AdminReadOnlyAlert tipo="obra" />}

        {/* <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'> */}

        {/* COMERCIAL */}
        <div className="border rounded-lg bg-white p-4 shadow-sm sm:p-4 w-full flex flex-col gap-3 mb-6">
          <p className="font-medium">Información Comercial</p>
          <hr className='mb-1'></hr>

          {/* Campo Fecha visita */}
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <div className="flex-1 min-w-0 sm:min-w-[180px] max-w-full sm:max-w-[200px]">
              <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Fecha visita*</label>
              <input
                type="date"
                id="FechaVisita"
                className="inputModal truncate w-full p-2 border rounded bg-white mb-0"
                value={obra.FechaVisita?.slice(0, 10) || todayLocal()}
                onChange={(e) => setObra({ ...obra, FechaVisita: e.target.value })}
              />
            </div>
            <div className="flex-1 min-w-0 sm:min-w-[180px] max-w-full">
              <ComercialDropDown selectedObra={obra} setSelectedObra={setObra} infoComerciales={infoComerciales} comercialIdsPermitidos={comercialIdsPermitidos} />
            </div>
            <div className="flex-1 min-w-0 sm:min-w-[180px] max-w-full">
              <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Nombre Técnico Comercial (BME)*</label>
              <input type="text" className="truncate inputModal shadow-sm w-full p-2 border rounded bg-white mb-0" value={obra.TecnicoComercial || ''} onChange={e => setObra({ ...obra, TecnicoComercial: e.target.value })} />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <div className="flex-1 min-w-[180px]">
              <DelegacionDropDown selectedObra={obra} setSelectedObra={setObra} idsSeccionesPermitidos={idsSeccionesPermitidos}/>
            </div>


            <CiudadDropDown
              required
              poblacionSeleccionada={obra.Poblacion}
              selectedObra={obra}
              setSelectedObra={setObra}
              ubicacionesPermitidas={ubicacionesPermitidas}
            />

            <div className="flex-1 min-w-[180px]">
              <ZonaDropDown
                required
                poblacionSeleccionada={obra.Poblacion}
                selectedObra={obra}
                setSelectedObra={setObra}
                ubicacionesPermitidas={ubicacionesPermitidas}
                isAdmin={isAdmin}
              />
            </div>
          </div>

          <div className="flex flex-col w-full">
            <div className="flex-1 min-w-[180px]">
              <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Observaciones Comercial</label>
              <textarea className="truncatecomentarios shadow-sm w-full p-2 border rounded bg-white mb-0 resize-y min-h-[60px] text-sm" value={obra.ObservacionesC || ''} onChange={e => setObra({ ...obra, ObservacionesC: e.target.value })} />
            </div>
          </div>
        </div>


        {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full mt-6"> */}

        {/* INSTALADOR*/}
        <div className="flex flex-col gap-4 border rounded-lg bg-white p-4 shadow-sm sm:p-4 w-full">
          
          <p className="font-medium">Información de la Obra</p>
          
          {/* Fila 1: Constructora + Nombre Obra */}
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <div className="flex-1 min-w-[180px]">
              <ConstructoraDropdown selectedObra={obra} setSelectedObra={setObra}/>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Nombre Obra*</label>
              <input type="text" className="truncate inputModal shadow-sm w-full p-2 border rounded bg-white mb-0" value={obra.Obra || ''} onChange={e => setObra({ ...obra, Obra: e.target.value })} />
            </div>
          </div>

          <hr />

          {/* Fila 2: Estado Obra + Tipo Material + Tipo Obra */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 w-full">
            <div className="min-w-[180px]">
              <EstadoObraDropdown selectedObra={obra} setSelectedObra={setObra} />
            </div>
            <div className="min-w-[180px]">
              <TipoMaterialDropdown selectedObra={obra} setSelectedObra={setObra} />
            </div>
            <div className="min-w-[180px]">
              <TipoObraDropdown selectedObra={obra} setSelectedObra={setObra} />
            </div>
          </div>

          <hr />

          {/* Grid con 3 columnas: Nombre + Teléfono + Mapa */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 w-full">
            {/* Columna 1 y 2: Nombre y Teléfono arriba, Email abajo */}

              {/* Fila 1: Nombre + Teléfono */}
              <div className="min-w-[180px]">
                <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Nombre*</label>
                <input type="text" className="truncate inputModal shadow-sm w-full p-2 border rounded bg-white mb-0" value={obra.NombreCV || ''} onChange={e => setObra({ ...obra, NombreCV: e.target.value })} />
              </div>
              <div className="min-w-[180px]">
                <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Teléfono*</label>
                <input 
                  type="tel" 
                  inputMode="numeric"
                  pattern="[0-9]{9,15}"
                  maxLength={9}
                  minLength={9}
                  className="inputModal shadow-sm w-full p-2 border rounded bg-white mb-0 truncate"
                  value={obra.TelefonoCV || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      setObra({ ...obra, TelefonoCV: value });
                    }
                  }}
                />
              </div>
              
              {/* Fila 2: Email (ocupa ambas columnas) */}
              <div className="min-w-[180px]">
                <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Email*</label>
                <input
                  type="email"
                  id="Email"
                  name="Email"
                  inputMode="email"
                  autoComplete="email"
                  spellCheck={false}
                  required
                  className={`inputModal shadow-sm w-full p-2 border rounded bg-white mb-0 truncate ${
                    emailError ? 'border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="nombre@dominio.com"
                  value={obra.EmailCV || ''}
                  onChange={(e) => {
                    const v = e.target.value.replace(/^\s+/, '');
                    setObra({ ...obra, EmailCV: v });
                    
                    if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
                      setEmailError('Email inválido');
                    } else {
                      setEmailError('');
                    }
                  }}
                  onBlur={(e) => {
                    const v = e.target.value.trim().toLowerCase();
                    setObra({ ...obra, EmailCV: v });
                    
                    if (v) {
                      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                      if (!emailRegex.test(v)) {
                        setEmailError('Formato de email inválido. Debe incluir @ y un dominio válido (.com, .es, etc.)');
                      } else {
                        setEmailError('');
                      }
                    } else {
                      setEmailError('');
                    }
                  }}
                />
                {emailError && (
                  <p className="text-red-500 text-xs mt-1">{emailError}</p>
                )}
            </div>

          </div>

          
          {/* Layout 2 columnas */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full">
            <div className="lg:col-span-2 flex flex-col gap-4">
              
              {/* Número y Ubicación - En móvil uno debajo del otro, desde md en la misma fila */}
              <div className="flex flex-col md:flex-row gap-3">
                <div className="w-full md:w-32">
                  <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Número</label>
                  <input
                    type="text"
                    inputMode="text"
                    maxLength={6}
                    minLength={1}
                    className="inputModal shadow-sm w-full p-2 border rounded bg-white mb-0 truncate"
                    value={obra.NumeroDireccionInput || obra.NumeroDireccion || ''}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Permitir escribir cualquier cosa, pero solo guardar si es válido
                      setObra({
                        ...obra,
                        NumeroDireccionInput: value
                      });
                      if (/^\d+$/.test(value)) {
                        setObra(prev => ({ ...prev, NumeroDireccion: value, NumeroDireccionInput: value }));
                      } else if (/^s\/?n$/i.test(value)) {
                        setObra(prev => ({ ...prev, NumeroDireccion: value.toUpperCase(), NumeroDireccionInput: value.toUpperCase() }));
                      } else if (value === '') {
                        setObra(prev => ({ ...prev, NumeroDireccion: '', NumeroDireccionInput: '' }));
                      } else {
                        setObra(prev => ({ ...prev, NumeroDireccion: '', NumeroDireccionInput: value }));
                      }
                    }}
                  />
                </div>

                <div className='flex-1 min-w-[180px]'>
                  <label className="block mb-1 text-xs font-medium text-gray-700 truncate">
                    Ubicación / Dirección
                  </label>
                  <input
                    type="text"
                    placeholder="Ej: Calle Mayor 123, Madrid"
                    className="inputModal w-full shadow-sm p-2 border rounded bg-white truncate"
                    value={obra.Direccion || ''}
                    onChange={(e) => setObra({ ...obra, Direccion: e.target.value })}
                  />
                </div>
              </div>

              <div className="w-full">

                <MapaUbicacion obra={obra} setObra={setObra} />

              </div>

            </div>

            <div className="lg:col-span-1 flex flex-col">
              <label
                  htmlFor="comentarios-obra"
                  className="block mb-1 text-xs font-medium text-gray-700 truncate"
                >
                  Comentarios Obra
              </label>
              <textarea
                id="comentarios-obra"
                className="truncatecomentarios shadow-sm w-full p-2 border rounded bg-white mb-0 resize-y min-h-[60px] text-sm flex-1"
                value={obra.ObservacionesCV || ''}
                onChange={e => setObra({ ...obra, ObservacionesCV: e.target.value })}
                placeholder="Añade observaciones, requisitos, etc."
              />

            </div>
          </div>


        </div>
    


        {/* INFORMACIÓN SUBCONTRATAS */}
        {/* Encabezado */}
        <div className="flex items-center mb-2 mt-8">
          <p className="font-medium">Información de las Subcontratas</p>
        </div>

        {/* 🔄 Mensaje de carga mientras se cargan las subcontratas */}
        {loadingSubcontratas && (
          <div className="flex items-center justify-center py-12 bg-white rounded-lg border shadow-sm">
            <div className="flex flex-col items-center gap-3">
              <svg className="h-8 w-8 animate-spin text-blue-600" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              <p className="text-gray-600 text-sm font-medium">Cargando subcontratas...</p>
            </div>
          </div>
        )}
    
        {/* GRID: los formularios + la tarjeta "Añadir" como un item más */}
        {!loadingSubcontratas && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subcontratas.map((sc, idx) => {
            // Validar si la subcontrata está completa (solo nombre y especialidad obligatorios)
            const isSubcontrataComplete = 
              sc.nombre?.trim() &&
              sc.especialidad?.trim();

            return (
            <div
              key={idx}
              className="border rounded-lg bg-white p-4 shadow-sm flex flex-col gap-4"
            >
              <div className="flex justify-between items-center gap-2">
                <h4 className="font-semibold text-sm text-gray-700">
                  Subcontrata {idx + 1}
                </h4>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        setCreandoVisitaIdx(idx);
                        setErrorMsg('');
                        
                        // Validar campos obligatorios de la obra
                        if (!obra.IdComercial || !obra.FechaVisita || !obra.IdObra) {
                          throw new Error('Completa los campos obligatorios de la obra primero');
                        }
                        
                        // Crear la visita directamente (sin guardar toda la obra)
                        const nuevaVisita = {
                          IdComercial: obra.IdComercial,
                          IdCliente: sc.IdCliente,
                          FechaVisita: obra.FechaVisita,
                          IdObra: obra.IdObra,
                          NumeroDireccion: obra.NumeroDireccion || '',
                          // ObservacionesC: sc.Comentarios || '',
                          Comentarios: sc.Comentarios || '',
                          Direccion: obra.Direccion || '',
                          LatLong: obra.LatLong || '',
                        };

                        const res = await fetch(`${getApiUrl('/api/ActividadComercialPUT/actividad-comercial-obra')}`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(nuevaVisita)
                        });

                        if (!res.ok) {
                          const errorData = await res.json();
                          throw new Error(errorData.error || 'Error al crear la visita');
                        }
                        
                        const visitaCreada = await res.json();
                        const idVisita = visitaCreada.IdOferta || visitaCreada.id;
                        
                        // Refrescar la lista de visitas en segundo plano
                        if (refreshVisitas) {
                          refreshVisitas().catch(err => console.error('Error al refrescar visitas:', err));
                        }
                        
                        // ⏱️ Esperar 3 segundos antes de mostrar el modal para asegurar que la visita esté creada
                        await new Promise(resolve => setTimeout(resolve, 3000));
                        
                        // Mostrar modal después del delay
                        setCreandoVisitaIdx(null);
                        setVisitaCreadaId(idVisita);
                        
                      } catch (error) {
                        // console.error('Error al crear visita:', error);
                        setCreandoVisitaIdx(null);
                        setErrorMsg(error.message || 'Error al crear la visita');
                        setTimeout(() => setErrorMsg(''), 3000);
                      }
                    }}
                    disabled={!isSubcontrataComplete || creandoVisitaIdx !== null}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      isSubcontrataComplete && creandoVisitaIdx === null
                        ? 'bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100'
                        : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                    }`}
                    title={
                      creandoVisitaIdx !== null
                        ? 'Creando visita...'
                        : isSubcontrataComplete
                        ? 'Crear visita para esta subcontrata'
                        : 'Completa todos los campos obligatorios para crear una visita'
                    }
                  >
                    {creandoVisitaIdx === idx ? 'Creando...' : 'Crear visita'}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeSubcontrata(idx)}
                    disabled={subcontratas.length === 1}
                    className={`p-2 rounded-xl ${
                      subcontratas.length === 1
                        ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                        : 'bg-red-50 text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-200'
                    }`}
                    aria-disabled={subcontratas.length === 1}
                    title={subcontratas.length === 1 ? 'Debe existir al menos una subcontrata' : 'Eliminar'}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Campos principales */}
              <div className="flex flex-col gap-4">

                <ClienteDropdownSubcontrata
                  subcontrata={sc}
                  index={idx}
                  updateSubcontrata={updateSubcontrata}
                />

                {/* <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Nombre Subcontrata*
                  </label>
                  <input
                    type="text"
                    className="inputModal w-full p-2 border rounded bg-white"
                    value={sc.nombre}
                    onChange={(e) => updateSubcontrata(idx, "nombre", e.target.value)}
                  />
                </div> */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Especialidad*
                  </label>
                  <input
                    type="text"
                    className="inputModal w-full p-2 border rounded bg-white"
                    value={sc.especialidad}
                    onChange={(e) =>
                      updateSubcontrata(idx, "especialidad", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Contacto */}
              <div className="flex flex-col gap-4">
                <p className="text-xs font-bold text-gray-500">Persona de contacto:</p>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Nombre
                  </label>
                  <input
                    type="text"
                    className="inputModal w-full p-2 border rounded bg-white"
                    value={sc.contacto.nombre}
                    onChange={(e) =>
                      updateSubcontrata(idx, "contacto.nombre", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    className="inputModal w-full p-2 border rounded bg-white"
                    value={sc.contacto.telefono}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^\d*$/.test(v))
                        updateSubcontrata(idx, "contacto.telefono", v);
                    }}
                    maxLength={15}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    className={`inputModal w-full p-2 border rounded bg-white ${
                      subcontrataEmailErrors[idx] ? 'border-red-500 focus:ring-red-500' : ''
                    }`}
                    placeholder='nombre@dominio.com'
                    value={sc.contacto.email}
                    onChange={(e) => {
                      const v = e.target.value.replace(/^\s+/, '');
                      updateSubcontrata(idx, "contacto.email", v);
                      
                      // Validar email en tiempo real (solo si hay contenido)
                      if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
                        setSubcontrataEmailErrors(prev => ({
                          ...prev,
                          [idx]: 'Email inválido'
                        }));
                      } else {
                        setSubcontrataEmailErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors[idx];
                          return newErrors;
                        });
                      }
                    }}
                    onBlur={(e) => {
                      const v = e.target.value.trim().toLowerCase();
                      updateSubcontrata(idx, "contacto.email", v);
                      
                      // Validación más estricta en blur (solo si hay contenido)
                      if (v) {
                        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                        if (!emailRegex.test(v)) {
                          setSubcontrataEmailErrors(prev => ({
                            ...prev,
                            [idx]: 'Formato de email inválido. Debe incluir @ y un dominio válido (.com, .es, etc.)'
                          }));
                        } else {
                          setSubcontrataEmailErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[idx];
                            return newErrors;
                          });
                        }
                      } else {
                        // Si está vacío, limpiar el error (es opcional)
                        setSubcontrataEmailErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors[idx];
                          return newErrors;
                        });
                      }
                    }}
                  />
                  {subcontrataEmailErrors[idx] && (
                    <p className="text-red-500 text-xs mt-1">{subcontrataEmailErrors[idx]}</p>
                  )}
                </div>

                <div className="flex-1 min-w-[180px]">
                  <label className="block text-xs font-medium text-gray-600 mb-1 truncate">Comentarios</label>
                  <textarea 
                    className="truncatecomentarios shadow-sm w-full p-2 border rounded bg-white mb-0 resize-y min-h-[60px] text-sm" 
                    value={sc.Comentarios || ''} 
                    onChange={e => updateSubcontrata(idx, 'Comentarios', e.target.value)} 
                  />
                </div>

              </div>
            </div>
            );
          })}

        {/* Tarjeta "Añadir" como un item del grid (centrado V/H) */}
        {subcontratas.length < 5 && (
          <div className="border-2 border-dashed rounded-lg p-4 bg-white/60 min-h-[220px] flex items-center justify-center" style={{borderColor: '#4299e1'}}>
            <button
              type="button"
              onClick={addSubcontrata}
              className="w-16 h-16 flex items-center justify-center rounded-full border border-blue-400 text-blue-600 hover:bg-blue-50"
              title="Añadir subcontrata"
              aria-label="Añadir subcontrata"
            >
              {/* Icono SVG (plus) */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="w-30 h-30"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {/* círculo opcional para look más “botón” */}
                <circle cx="12" cy="12" r="10" className="opacity-0" />
                <line x1="12" y1="7" x2="12" y2="17" />
                <line x1="7" y1="12" x2="17" y2="12" />
              </svg>
            </button>
          </div>
        )}

        </div>
        )}


        {/* </div> */}

{/* Botones de acción (estilo minimal) */}
        <div className="flex justify-end mt-2 gap-2 sm:gap-2 pt-3 mb-5 md:mb-0">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-lg text-red-600 border bg-red-50 hover:bg-red-100 border-red-200 text-sm font-medium transition-colors"
            title="Cerrar sin guardar"
          >
            Cancelar
          </button>

          {(() => {
            const hasSubcontrataEmailErrors = Object.keys(subcontrataEmailErrors).length > 0;
            const canSave = isModified && !saving && !successMsg && !errorMsg && !emailError && !hasSubcontrataEmailErrors && !isAdmin; // 🔒 Deshabilitar para admins
            const disabledReason = isAdmin
              ? 'Los administradores no pueden editar obras'
              : !isModified
              ? 'No hay cambios para guardar'
              : successMsg
              ? 'La operación ya se completó'
              : errorMsg
              ? 'Corrige los errores antes de continuar'
              : emailError
              ? 'Email del contacto inválido'
              : hasSubcontrataEmailErrors
              ? 'Emails de subcontratas inválidos'
              : saving
              ? 'Guardando…'
              : '';
            return (
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave}
                aria-busy={saving || undefined}
                title={!canSave ? disabledReason : 'Actualizar obra'}
                className={`px-3 py-1.5 rounded-lg text-blue-600 border bg-blue-50 hover:bg-blue-100 border-blue-200 text-sm font-medium transition-colors ${
                  canSave
                    ? 'hover:bg-blue-100 border-blue-200'
                    : 'bg-white text-gray-400 border-gray-200 cursor-not-allowed'
                }`}
              >
                {saving ? (
                  <>
                    <svg className="h-3 w-3 inline-block mr-2 animate-spin align-[-2px]" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    Guardando…
                  </>
                ) : (
                  'Guardar'
                )}
              </button>
            );
          })()}
        </div>
        {/* Overlay de Success */}
        {successMsg && (
          <div className="fixed inset-0 z-[1500] flex items-center justify-center">
            <div className="bg-gray-600 rounded-2xl px-6 py-5 shadow-2xl w-auto max-w-md">
              <div className="flex flex-col items-center text-center gap-3">
                <CircleCheck className="w-20 h-20 text-green-500" aria-hidden="true" />
                <p role="alert" aria-live="polite" className="text-white text-base font-medium px-2">
                  {successMsg}
                </p>
              </div>
            </div>
          </div>

          // <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          //   <div className="bg-white rounded-2xl px-8 py-6 shadow-2xl w-auto max-w-md">
          //     <div className="flex flex-col items-center text-center gap-4">
          //       <CircleCheck className="w-16 h-16 text-green-500" aria-hidden="true" />
          //       <h3 className="text-xl font-bold text-gray-800">
          //         {successMsg}
          //       </h3>
          //     </div>
          //   </div>
          // </div>
        )}

        {/* Overlay de Error */}
        {errorMsg && (
          <div 
            className="fixed inset-0 flex items-center justify-center cursor-pointer"
            onClick={() => setErrorMsg('')}
            title="Haz clic para cerrar"
            style={{zIndex: 1500}}

          >
            <div className="bg-gray-600 rounded-2xl px-6 py-5 shadow-2xl w-auto max-w-md">
              <div className="flex flex-col items-center text-center gap-3">
                <CircleX className="w-20 h-20 text-red-500" aria-hidden="true" />
                {errorMsg.includes('TITULO||') ? (
                  <div role="alert" aria-live="polite" className="text-white px-2 text-left w-full">
                    {(() => {
                      const parts = errorMsg.split('||');
                      const titulo = parts[1] || '';
                      const lista = parts[3] || '';
                      return (
                        <>
                          <p className="text-base font-medium mb-2">{titulo}</p>
                          <p className="text-sm whitespace-pre-line">{lista}</p>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p role="alert" aria-live="polite" className="text-white text-base font-medium px-2">
                    {errorMsg}
                  </p>
                )}
                <button
                  type="button"
                  className="mt-1 text-xs px-3 py-1 rounded-md bg-gray-600 text-white hover:bg-gray-500"
                  onClick={() => setErrorMsg("")}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Confirmación - Ir a Visita Creada */}
        {visitaCreadaId && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{zIndex: 1500}}>
            <div className="bg-white rounded-2xl px-8 py-6 shadow-2xl w-auto max-w-md">
              <div className="flex flex-col items-center text-center gap-4">
                <CircleCheck className="w-16 h-16 text-green-500" aria-hidden="true" />
                <h3 className="text-xl font-bold text-gray-800">
                  ¡Visita creada correctamente!
                </h3>
                <p className="text-gray-600 text-sm">
                  ¿Deseas ir a editarla ahora?
                </p>
                <div className="flex gap-3 mt-2 w-full">
                  <button
                    onClick={() => {
                      setVisitaCreadaId(null);
                    }}
                    className="flex-1 px-4 py-2 rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-sm font-medium transition-colors"
                  >
                    Quedarme aquí
                  </button>
                  <button
                    onClick={() => {
                      navigate(`/dashboard/visitas?visita=${visitaCreadaId}`);
                      setVisitaCreadaId(null);
                    }}
                    className="flex-1 px-4 py-2 rounded-lg text-white bg-blue-600 hover:bg-blue-700 text-sm font-medium transition-colors"
                  >
                    Ir a la visita
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      </div>
    </div>
  );
};

export default ObraContent;
